import { ComputedQuery, OptionalFiltersMap } from '@wix/bex-core';
import { observer } from 'mobx-react-lite';
import React, { ComponentType, ReactNode } from 'react';
import { EmptyStateProps, TextButton } from '@wix/design-system';
import { useCollectionContext } from '../CollectionContext';
import { CollectionEmptyStateBase } from '../CollectionEmptyStateBase';
import { MagnifyingGlassArt } from '../MagnifyingGlassArt';

export interface CommonEmptyStateProps extends Partial<EmptyStateProps> {
  component?: ComponentType<EmptyStateProps>;
  children?: ReactNode;
  /**
   * Text for "Reset Filters" button
   */
  actionButtonText?: string;
  /**
   * A "Reset filters" button click callback
   */
  onResetFiltersButtonClick?(params: { query: ComputedQuery }): unknown;
}

function _CommonEmptyState(props: CommonEmptyStateProps) {
  const { children, actionButtonText, onResetFiltersButtonClick, ...rest } =
    props;

  const state = useCollectionContext<unknown, OptionalFiltersMap>();

  const {
    translate: t,
    result: { originQuery },
  } = state;

  const { hasNonPersistentActiveFilters } = originQuery;

  let button;

  if (hasNonPersistentActiveFilters) {
    button = (
      <TextButton
        dataHook="empty-state-clear-filters-button"
        onClick={() => {
          onResetFiltersButtonClick?.({
            query: originQuery,
          });
          state.emitter.emit('beforeClearFilters', { origin: 'empty-state' });
          state.resetFiltersAndRefresh();
        }}
      >
        {actionButtonText ?? t('cairo.emptyState.reset.button')}
      </TextButton>
    );
  }

  return (
    <CollectionEmptyStateBase
      state={state}
      image={<MagnifyingGlassArt />}
      {...rest}
    >
      {button}
      {children}
    </CollectionEmptyStateBase>
  );
}

/**
 * @deprecated Use instead: `import { CollectionEmptyState } from '@wix/cairo';`
 */
export const CommonEmptyState = observer(_CommonEmptyState);
